import { COLORS } from "../shared/constants";
import { IconX } from "../shared/icons";
import { useState } from "react";
import * as api from "../api";

export default // ─── Reminder Form ───────────────────────────────────────────
function ReminderForm({ reminder, treatments, currentUser, onSave, onCancel, assessmentScales, clinicInfo }) {
  const isEdit = !!reminder;

  // Get assessment reminder defaults from clinic settings
  const assessDefaults = clinicInfo?.reminder_defaults?.assessment || { schedule_type: "PERIODIC", periodic_value: 1, periodic_unit: "WEEKS" };

  const getInitialForm = () => {
    if (reminder) {
      return {
        treatment_assignment_id: reminder.treatment_assignment_id || (treatments.length === 1 ? treatments[0].id : ""),
        reminder_type: reminder.reminder_type || "CUSTOM",
        label: reminder.label || "",
        schedule_type: reminder.schedule_type || "SESSION_BASED",
        session_interval: reminder.session_interval || "",
        periodic_value: reminder.periodic_value || "",
        periodic_unit: reminder.periodic_unit || "WEEKS",
        color: reminder.color || "orange",
      };
    }
    return {
      treatment_assignment_id: treatments.length === 1 ? treatments[0].id : "",
      reminder_type: "CUSTOM",
      label: "",
      schedule_type: "SESSION_BASED",
      session_interval: "",
      periodic_value: "",
      periodic_unit: "WEEKS",
      color: "orange",
    };
  };

  const [form, setForm] = useState(getInitialForm());
  const [errors, setErrors] = useState({});
  const [defaultSaved, setDefaultSaved] = useState(false);

  const handleChange = (f, v) => {
    const updated = { ...form, [f]: v };
    // Auto-set color and defaults based on type
    if (f === "reminder_type") {
      if (v === "MT_REDETERMINATION") {
        updated.color = "yellow";
        updated.label = "Motor Threshold Redetermination";
        updated.schedule_type = "SESSION_BASED";
        updated.session_interval = "";
        updated.periodic_value = "";
      } else if (v === "ASSESSMENT") {
        updated.color = "blue";
        if (!updated.label || updated.label === "Motor Threshold Redetermination") updated.label = "";
        // Apply assessment defaults
        updated.schedule_type = assessDefaults.schedule_type || "PERIODIC";
        if (updated.schedule_type === "PERIODIC") {
          updated.periodic_value = assessDefaults.periodic_value || 1;
          updated.periodic_unit = assessDefaults.periodic_unit || "WEEKS";
          updated.session_interval = "";
        } else {
          updated.session_interval = assessDefaults.session_interval || "";
          updated.periodic_value = "";
        }
      } else {
        updated.color = "orange";
      }
    }
    setForm(updated);
    setErrors({});
    setDefaultSaved(false);
  };

  const handleSubmit = () => {
    const errs = {};
    if (!form.treatment_assignment_id) errs.tx = "Required";
    if (!form.label.trim()) errs.label = "Required";
    if (form.schedule_type === "SESSION_BASED" && (!form.session_interval || form.session_interval < 1)) errs.interval = "Required";
    if (form.schedule_type === "PERIODIC" && (!form.periodic_value || form.periodic_value < 1)) errs.interval = "Required";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    onSave({
      ...reminder,
      ...form,
      session_interval: form.schedule_type === "SESSION_BASED" ? parseInt(form.session_interval) : null,
      periodic_value: form.schedule_type === "PERIODIC" ? parseInt(form.periodic_value) : null,
      periodic_unit: form.schedule_type === "PERIODIC" ? form.periodic_unit : null,
      is_active: true,
      created_by: currentUser.id,
    });
  };

  const handleSetAsDefault = async () => {
    const newDefaults = { schedule_type: form.schedule_type };
    if (form.schedule_type === "SESSION_BASED") {
      newDefaults.session_interval = parseInt(form.session_interval) || 0;
    } else {
      newDefaults.periodic_value = parseInt(form.periodic_value) || 1;
      newDefaults.periodic_unit = form.periodic_unit || "WEEKS";
    }
    try {
      await api.updateClinic({
        reminder_defaults: { ...clinicInfo?.reminder_defaults, assessment: newDefaults },
      });
      setDefaultSaved(true);
    } catch (e) {
      console.error("Failed to save defaults:", e);
    }
  };

  const selStyle = (hasErr) => ({ width: "100%", height: "38px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${hasErr ? COLORS.danger : COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface, boxSizing: "border-box" });

  return (
    <div style={{ padding: "14px", background: COLORS.primaryLight, borderRadius: "10px", border: `1px solid ${COLORS.primary}30`, marginBottom: "14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: COLORS.primary }}>{isEdit ? "Edit Reminder" : "Add Reminder"}</div>
        <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textSecondary }}><IconX /></button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "10px" }}>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Treatment {errors.tx && <span style={{ color: COLORS.danger }}>*</span>}</label>
          <select style={selStyle(errors.tx)} value={form.treatment_assignment_id} onChange={(e) => handleChange("treatment_assignment_id", e.target.value)}>
            <option value="">Select treatment…</option>
            {treatments.map((tx) => <option key={tx.id} value={tx.id}>{tx.protocol_name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Type</label>
          <select style={selStyle()} value={form.reminder_type} onChange={(e) => handleChange("reminder_type", e.target.value)}>
            <option value="MT_REDETERMINATION">MT Redetermination</option>
            <option value="ASSESSMENT">Clinical Assessment</option>
            <option value="CUSTOM">Other / Custom</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Label {errors.label && <span style={{ color: COLORS.danger }}>*</span>}</label>
          {form.reminder_type === "ASSESSMENT" && (assessmentScales || []).length > 0 ? (
            <select style={selStyle(errors.label)} value={form.label} onChange={(e) => handleChange("label", e.target.value)}>
              <option value="">Select scale...</option>
              {(assessmentScales || []).map(s => <option key={s.id} value={s.short_name}>{s.short_name} — {s.name}</option>)}
            </select>
          ) : (
            <input style={selStyle(errors.label)} value={form.label} onChange={(e) => handleChange("label", e.target.value)}
              placeholder={form.reminder_type === "ASSESSMENT" ? "e.g., PHQ-9, GAD-7" : "Reminder name"} />
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "12px" }}>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Schedule Type</label>
          <select style={selStyle()} value={form.schedule_type} onChange={(e) => handleChange("schedule_type", e.target.value)}>
            <option value="SESSION_BASED">Session-based</option>
            <option value="PERIODIC">Periodic interval</option>
          </select>
        </div>
        {form.schedule_type === "SESSION_BASED" ? (
          <div>
            <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Every N sessions {errors.interval && <span style={{ color: COLORS.danger }}>*</span>}</label>
            <input type="number" min="1" style={selStyle(errors.interval)} value={form.session_interval} onChange={(e) => handleChange("session_interval", e.target.value)} placeholder="e.g., 10" />
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
            <div>
              <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Every {errors.interval && <span style={{ color: COLORS.danger }}>*</span>}</label>
              <input type="number" min="1" style={selStyle(errors.interval)} value={form.periodic_value} onChange={(e) => handleChange("periodic_value", e.target.value)} placeholder="#" />
            </div>
            <div>
              <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Unit</label>
              <select style={selStyle()} value={form.periodic_unit} onChange={(e) => handleChange("periodic_unit", e.target.value)}>
                <option value="DAYS">Days</option>
                <option value="WEEKS">Weeks</option>
                <option value="MONTHS">Months</option>
              </select>
            </div>
          </div>
        )}
        <div>
          <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px", display: "block" }}>Color</label>
          <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
            {[
              { val: "yellow", label: "Yellow (MT)", bg: "#F0D060" },
              { val: "blue", label: "Blue (Assessment)", bg: "#7BAAF7" },
              { val: "orange", label: "Orange (Other)", bg: "#F0A050" },
            ].map((c) => (
              <button key={c.val} onClick={() => handleChange("color", c.val)}
                style={{
                  width: "32px", height: "32px", borderRadius: "8px", border: form.color === c.val ? `3px solid ${COLORS.text}` : `2px solid ${COLORS.border}`,
                  background: c.bg, cursor: "pointer",
                }}
                title={c.label} />
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <button onClick={handleSubmit}
          style={{ height: "36px", padding: "0 18px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
          {isEdit ? "Save Changes" : "Add Reminder"}
        </button>
        {form.reminder_type === "ASSESSMENT" && (
          <button onClick={handleSetAsDefault}
            style={{ height: "36px", padding: "0 14px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "12px", fontWeight: "500", cursor: "pointer" }}>
            {defaultSaved ? "✓ Default Saved" : "Set as Default"}
          </button>
        )}
        <button onClick={onCancel}
          style={{ height: "36px", padding: "0 14px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textSecondary, fontSize: "12px", fontWeight: "500", cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

